import React, { FC, useEffect, useState } from "react";
import { Placeholder } from "kanopi-components/Placeholder";
import { RouteComponentProps, useNavigate } from "@reach/router";
import { useIsActionAllowed } from "kw/hooks/useIsActionAllowed";
import { useMatchParams } from "kanopi-components/hooks/useMatchParams";

import {
  enquiryFields,
  questionFields,
  questionSetFields,
  claimProfileFields,
} from "kw/api/fragments";
import {
  QuestionFlowComponent,
  QuestionFlowTypeEnum,
} from "kw/components/QuestionFlow/QuestionFlowComponent";

type TClaimFlowPageProps = {
  policyId: string;
  questionSetKey: string;
  restart?: boolean;
};

export const ClaimFlowPage: FC<TClaimFlowPageProps & RouteComponentProps> = ({
  policyId,
  uri: prefix,
  restart = false,
}) => {
  const { questionSetKey } = useMatchParams(`${prefix}/:questionSetKey`);
  const policyClaimAllowedQuery = {
    policy: [
      {
        id: policyId,
      },
      {
        claimsAllowed: true,
      },
    ],
  };

  if (!restart && questionSetKey == undefined) {
    restart = true;
  }

  const startPolicyClaimQuery = {
    startClaim: [
      {
        input: {
          policyId,
          questionSetKey,
          restart,
        },
      },
      {
        currentEnquiry: {
          ...enquiryFields,
          questionSet: {
            ...questionSetFields,
            questions: {
              ...questionFields,
            },
          },
        },
        claimProfile: claimProfileFields,
        history: {
          questionSet: {
            id: true,
            key: true,
            label: true,
          },
        },
        maxSets: true,
        currentSet: true,
      },
    ],
  };
  const [currentMaxSet, setCurrentMaxSet] = useState({
    currentSet: 0,
    maxSets: 1,
  });
  const { queryIsActionAllowed, data } = useIsActionAllowed(
    policyClaimAllowedQuery,
    "policy.claimsAllowed"
  );
  const { isAllowed: isPolicyClaimAllowed, loading: isCheckClaimAllowedLoading } =
    data || {};
  const navigate = useNavigate();
  const onCurrentMaxSetChange = (currentSet, maxSets) => {
    const _totalSets = { ...currentMaxSet };
    _totalSets.currentSet = currentSet;
    _totalSets.maxSets = maxSets;
    setCurrentMaxSet(_totalSets);
  };

  const handleEmptyClaimsQuestionSets = () => {
    navigate("/claims/make-a-claim/error");
  };

  useEffect(() => {
    queryIsActionAllowed();
  }, []);

  useEffect(() => {
    if (isPolicyClaimAllowed === false && !isCheckClaimAllowedLoading) {
      navigate("/policies");
    }
  }, [isPolicyClaimAllowed, isCheckClaimAllowedLoading]);

  return !isPolicyClaimAllowed && isCheckClaimAllowedLoading ? (
    <Placeholder loading={true} />
  ) : isPolicyClaimAllowed ? (
    <QuestionFlowComponent
      isFlowLayout={true}
      actionFlowType={QuestionFlowTypeEnum.Claim}
      prefix={prefix}
      finishedRoute={`/claims/make-a-claim/${policyId}/summary`}
      defaultBackLocation={"/claims"}
      actionQuery={startPolicyClaimQuery}
      onQuestionFlowEmpty={handleEmptyClaimsQuestionSets}
      questionFlowResponseKey="startClaim"
      profileFieldName="claimProfile"
      questionSetKey={questionSetKey}
      steps={[
        {
          label: "Your claim details",
          active: true,
          current: currentMaxSet.currentSet,
          total: currentMaxSet.maxSets,
        },
        { label: "Review and submit" },
      ]}
      onCurrentAndMaxSetChange={onCurrentMaxSetChange}
    />
  ) : (
    <></>
  );
};
